import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import i18n from 'i18n-react';
import { renderToString } from 'react-dom/server';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { APIHandler, SharingPopup } from 'C4';

// Social media logo and color configurations
const socialMediaConfig = {
  default: {
    logo: ['far', 'globe'],
    color: '', // styling in css
  },
  facebook: {
    logo: ['fab', 'facebook-f'],
    color: '#316FF6',
  },
  twitter: {
    logo: ['fab', 'x-twitter'],
    color: '#000000',
  },
  x: {
    logo: ['fab', 'x-twitter'],
    color: '#000000',
  },
  pinterest: {
    logo: ['fab', 'pinterest-p'],
    color: '#E60023',
  },
  instagram: {
    logo: ['fab', 'instagram'],
    color: '', // styling in css
  },
  linkedin: {
    logo: ['fab', 'linkedin-in'],
    color: '#0077b5',
  },
  // youtu.be link shorteners
  youtu: {
    logo: ['fab', 'youtube'],
    color: '#FF0000',
  },
  youtube: {
    logo: ['fab', 'youtube'],
    color: '#FF0000',
  },
  tikTok: {
    logo: ['fab', 'tiktok'],
    color: '#000000',
  },
  tiktok: {
    logo: ['fab', 'tiktok'],
    color: '#000000',
  },
  flickr: {
    logo: ['fab', 'flickr'],
    color: '#FF0084',
  },
  // Add new social media platforms here
};

// Formats https link to string for use on social media buttons
const formatLink = (link) => {
  const string = link.split('/').filter((str) => str !== '');
  string[1] = string[1].replace('www.', '');
  if (string[1].includes('twitter') || string[1].includes('instagram') || string[1].includes('tikTok')) {
    if (!string[string.length - 1].includes('twitter') && !string[string.length - 1].includes('instagram') || !string[string.length - 1].includes('tikTok')) {
      return '@'.concat(string[string.length - 1]);
    }
  }
  if (string[1].includes('pinterest')) {
    return 'Pinterest';
  }
  if (string[1].includes('linkedin')) {
    return 'LinkedIn';
  }
  Object.keys(socialMediaConfig).forEach((key) => {
    if (string[1].includes(key)) {
      [string[1]] = string[1].split('.');
      string[1] = string[1].charAt(0).toUpperCase() + string[1].slice(1);
    }
  });
  return string[string.length - 1];
};

const fetchYouTubeTitle = (link) => {
  const [title, setTitle] = useState('YouTube');
  if (link) {
    fetch(`https://noembed.com/embed?dataType=json&url=${link}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.title) setTitle(data.title);
      })
      .catch((error) => console.log(error));
  }
  return title;
};

// Button component
const WebsiteButton = ({ link, partnerColor }) => {
  let {
    default: { logo, color },
  } = socialMediaConfig;
  let type = 'officialWebsite';
  let id = link;

  if (partnerColor) {
    color = partnerColor;
  }

  Object.keys(socialMediaConfig).forEach((key) => {
    if (link.includes(key)) {
      logo = socialMediaConfig[key].logo;
      color = socialMediaConfig[key].color;
      type = 'socialMedia';
      id = key;
    }
  });

  if ((link.includes('youtube') && (link.includes('list') || link.includes('watch?v='))) || link.includes('youtu.be')) {
    type = 'youtubePlaylist';
    return (
      <a
        href={link}
        data-fancybox
        data-options={JSON.stringify({
          buttons: ['thumbs', 'share', 'close'],
          share: {
            tpl: renderToString(<SharingPopup url={link} downloadable={false} />),
          },
        })}
      >
        <button
          id={id}
          type="button"
          className={type}
          style={{
            background: color,
          }}
        >
          <FontAwesomeIcon className="buttonLogo" icon={logo} />
          <div className="buttonText">{fetchYouTubeTitle(link)}</div>
        </button>
      </a>
    );
  }

  return (
    <button
      id={id}
      type="button"
      className={type}
      key={link}
      onClick={() => {
        window.open(link, '_blank', 'noreferrer');
      }}
      style={{
        background: color,
      }}
    >
      <FontAwesomeIcon className="buttonLogo" icon={logo} />
      {type === 'officialWebsite' && <div className="buttonText">{link}</div>}
      {type === 'socialMedia' && <div className="buttonText">{formatLink(link)}</div>}
    </button>
  );
};

// Social pop-up component
const SocialPopup = ({ appId }) => {
  const [socialMedia, setSocialMedia] = useState([]);
  const [officialWebsites, setOfficialWebsites] = useState([]);
  const [videoPlaylist, setVideoPlaylist] = useState([]);
  const [color, setColor] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const getAppData = () => {
      const socials = [];
      const websites = [];
      const videos = [];
      APIHandler.getAppInfo(appId).then((res) => {
        if (res) {
          Object.keys(socialMediaConfig).forEach((key) => {
            // Fetch social media links for all platforms in socialMediaConfig (in order)
            if (res.app[0][key]) {
              socials.push(res.app[0][key]);
            }
          });
          if (res.app[0].clientWebsite) {
            websites.push(res.app[0].clientWebsite);
          }
          if (res.app[0].youtube_playlist) {
            videos.push(res.app[0].youtube_playlist);
          }
          setOfficialWebsites(websites);
          setSocialMedia(socials);
          setVideoPlaylist(videos);
          setColor(res.app[0].color); // Set partner specific color (for Partner Modules)
        }
      });
    };
    getAppData();
  }, [appId]);

  return (
    <div className="socialPopup" ref={ref}>
      <div className="popupScreen">
        {videoPlaylist.length > 0 && (
          <div>
            <h1 style={{ color: color }}>{i18n.translate('socialPopup.videos').toUpperCase()}</h1>
            {videoPlaylist.map((link) => (
              <WebsiteButton key={link} link={link} />
            ))}
          </div>
        )}
        {socialMedia.length > 0 && (
          <div>
            <h1 style={{ color: color }}>{i18n.translate('socialPopup.socialNetworks').toUpperCase()}</h1>
            {socialMedia.map((link) => (
              <WebsiteButton key={link} link={link} />
            ))}
          </div>
        )}
        {officialWebsites.length > 0 && (
          <div>
            {officialWebsites.length === 1 ? (
              <h1 style={{ color: color }}>{i18n.translate('socialPopup.officialWebsite').toUpperCase()}</h1>
            ) : (
              <h1 style={{ color: color }}>{i18n.translate('socialPopup.officialWebsites').toUpperCase()}</h1>
            )}
            {officialWebsites.map((link) => (
              <WebsiteButton key={link} link={link} partnerColor={color} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SocialPopup;

WebsiteButton.propTypes = {
  link: PropTypes.string.isRequired,
};

SocialPopup.propTypes = {
  appId: PropTypes.string.isRequired,
};
